/** PUB-01 Accueil · PUB-02 Catalogue public (sans prix) · PUB-04 Fiche produit publique · PUB-06 Contact. */
import { useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Alert, Badge, Button, Card, Icon, Pagination, SearchField, SelectField, StockIndicator, TextareaField, TextField } from '@myron/design-system';
import { Boxes, Lock, Package, Tags, Truck, Warehouse } from 'lucide-react';
import type { Page, S } from '@myron/contracts';
import { get, post } from '../../shared/api/client';
import { useSession } from '../../shared/auth/session';
import { EntetePage, ErreurFormulaire, EtatRequete } from '../../shared/ui/composants';
import { detailConditionnement, type ProduitPublic } from '../pro/api';

const LIB_COND: Record<string, string> = { PALETTE: 'Palette', CARTON: 'Carton', PACK: 'Pack' };

function useReferentiels() {
  const categories = useQuery({ queryKey: ['public', 'categories'], queryFn: () => get<S<'Categorie'>[]>('/catalogue/categories'), staleTime: 300_000 });
  const marques = useQuery({ queryKey: ['public', 'marques'], queryFn: () => get<S<'Marque'>[]>('/catalogue/marques'), staleTime: 300_000 });
  return { categories, marques };
}

export function Accueil() {
  const { categories } = useReferentiels();
  const { session, connecte } = useSession();
  return (
    <>
      <section className="my-section my-section--sable">
        <div className="my-conteneur my-pile my-pile--large">
          <div className="my-pile" style={{ maxWidth: 760 }}>
            <span className="my-filet" aria-hidden />
            <h1 className="my-display">Le grossiste alimentaire qui livre à la palette.</h1>
            <p className="my-corps-large my-secondaire">
              Boissons, épicerie et produits alimentaires pour les cafés, restaurants, commerces et collectivités. Trois entrepôts en Île-de-France,
              des prix négociés selon votre activité, la palette en priorité et le carton selon les références.
            </p>
            <div className="my-ligne">
              <Link to="/catalogue" className="my-btn my-btn--primaire">Voir le catalogue</Link>
              {connecte && session
                ? <Link to={session.utilisateur.type === 'INTERNE' ? '/admin' : '/pro'} className="my-btn my-btn--secondaire">Accéder à mon espace</Link>
                : <Link to="/devenir-client" className="my-btn my-btn--secondaire">Ouvrir un compte professionnel</Link>}
            </div>
          </div>
        </div>
      </section>
      <section className="my-section">
        <div className="my-conteneur my-pile my-pile--large">
          <h2 className="my-titre-2">Pourquoi MYRON</h2>
          <div className="my-grille">
            {[
              { i: Boxes, t: 'La palette en priorité', d: 'Les meilleurs prix sont à la palette. Le carton reste possible sur les références qui le permettent.' },
              { i: Tags, t: 'Vos prix, après connexion', d: 'Tarifs de groupe, tarifs négociés et remises de volume, calculés pour votre entreprise.' },
              { i: Warehouse, t: 'Trois entrepôts', d: 'Mantes-la-Ville, Buchelay et Gennevilliers : livraison ou retrait sur créneau.' },
              { i: Truck, t: 'Paiement simple', d: 'Virement, carte en ligne ou carte au retrait. Le stock est réservé dès la confirmation du paiement.' },
            ].map((x) => (
              <Card key={x.t} as="article">
                <div className="my-pile my-pile--serre">
                  <Icon icon={x.i} />
                  <h3 className="my-titre-4">{x.t}</h3>
                  <p className="my-secondaire">{x.d}</p>
                </div>
              </Card>
            ))}
          </div>
          {categories.data && (
            <nav aria-label="Rayons du catalogue" className="my-pile my-pile--serre">
              <h2 className="my-titre-3">Nos rayons</h2>
              <ul className="my-ligne" style={{ listStyle: 'none', padding: 0 }}>
                {categories.data.map((c) => (
                  <li key={c.id}><Link className="my-btn my-btn--secondaire my-btn--compact" to={`/catalogue?categorieId=${c.id}`}>{c.libelle}</Link></li>
                ))}
              </ul>
            </nav>
          )}
        </div>
      </section>
    </>
  );
}

export function CarteProduitPublic({ p }: { p: ProduitPublic }) {
  return (
    <Card as="article" className="my-pile" data-testid="carte-produit">
      <div className="my-visuel" aria-hidden><Icon icon={Package} /></div>
      <div className="my-pile my-pile--serre">
        <span className="my-legende my-secondaire">{p.marque} · {p.categorie}</span>
        <h3 className="my-titre-4"><Link to={`/produits/${p.slug}`}>{p.libelle}</Link></h3>
        <span className="my-petit my-secondaire">Réf. {p.sku} · {p.contenance}</span>
        <div className="my-ligne">
          {(p.conditionnements ?? []).map((c) => <Badge key={c.type} ton={c.type === 'PALETTE' ? 'marque' : 'neutre'}>{LIB_COND[c.type!]}</Badge>)}
        </div>
        {p.disponibilite && <StockIndicator etat={p.disponibilite} />}
      </div>
    </Card>
  );
}

export function CataloguePublic() {
  const [params, setParams] = useSearchParams();
  const { categories, marques } = useReferentiels();
  const [saisie, setSaisie] = useState(params.get('q') ?? '');
  const filtres = { q: params.get('q') ?? undefined, categorieId: params.get('categorieId') ?? undefined, marqueId: params.get('marqueId') ?? undefined, page: Number(params.get('page') ?? 1), taille: 12 };
  const q = useQuery({ queryKey: ['public', 'catalogue', filtres], queryFn: () => get<Page<ProduitPublic>>('/catalogue/produits', filtres), placeholderData: (x) => x });
  const changer = (cle: string, v: string | undefined) => {
    const n = new URLSearchParams(params);
    if (v) n.set(cle, v); else n.delete(cle);
    if (cle !== 'page') n.delete('page');
    setParams(n);
  };
  return (
    <div className="my-conteneur">
      <EntetePage titre="Catalogue" sousTitre="Prix et conditions réservés aux professionnels connectés." />
      <Alert ton="info" titre="Vous êtes professionnel ?" action={<div className="my-ligne"><Link to="/connexion?retour=%2Fpro%2Fcatalogue">Se connecter pour voir les prix</Link><Link to="/devenir-client">Ouvrir un compte</Link></div>}>
        Les prix, remises et quantités disponibles s’affichent après connexion à votre espace professionnel.
      </Alert>
      <form className="my-form-ligne" style={{ margin: '24px 0', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }} role="search" onSubmit={(e) => { e.preventDefault(); changer('q', saisie.trim() || undefined); }}>
        <div className="my-pile my-pile--serre">
          <SearchField label="Rechercher un produit" placeholder="Produit, marque, référence…" value={saisie} onChange={(e) => setSaisie(e.target.value)} />
          <Button type="submit" variante="secondaire" compact>Rechercher</Button>
        </div>
        <SelectField label="Rayon" value={filtres.categorieId ?? ''} onChange={(e) => changer('categorieId', e.target.value || undefined)} options={[{ value: '', label: 'Tous les rayons' }, ...(categories.data ?? []).map((c) => ({ value: c.id!, label: c.libelle! }))]} />
        <SelectField label="Marque" value={filtres.marqueId ?? ''} onChange={(e) => changer('marqueId', e.target.value || undefined)} options={[{ value: '', label: 'Toutes les marques' }, ...(marques.data ?? []).map((m) => ({ value: m.id!, label: m.libelle! }))]} />
      </form>
      <EtatRequete
        requete={q}
        vide={(d) => d.donnees.length === 0}
        titreVide="Aucun produit ne correspond"
        texteVide="Modifiez votre recherche ou vos filtres."
        actionVide={<Button variante="secondaire" onClick={() => { setSaisie(''); setParams(new URLSearchParams()); }}>Effacer les filtres</Button>}
        enfants={(d) => (
          <div className="my-pile">
            <p className="my-petit my-secondaire" aria-live="polite">{d.total} produit(s)</p>
            <div className="my-grille">{d.donnees.map((p) => <CarteProduitPublic key={p.id} p={p} />)}</div>
            <Pagination page={d.page} taille={d.taille} total={d.total} onPage={(p) => changer('page', String(p))} />
          </div>
        )}
      />
    </div>
  );
}

export function FicheProduitPublic() {
  const { slug = '' } = useParams();
  const { connecte, estClient } = useSession();
  const q = useQuery({ queryKey: ['public', 'produit', slug], queryFn: () => get<ProduitPublic>(`/catalogue/produits/${slug}`) });
  return (
    <div className="my-conteneur">
      <EtatRequete
        requete={q}
        enfants={(p) => {
          return (
            <>
              <EntetePage titre={p.libelle!} sousTitre={`${p.marque} · ${p.contenance} · Réf. ${p.sku}`} fil={[{ libelle: 'Catalogue', vers: '/catalogue' }, { libelle: p.libelle! }]} />
              <div className="my-grille-2">
                <div className="my-pile">
                  <div className="my-visuel" aria-hidden><Icon icon={Package} /></div>
                  {p.description && <p>{p.description}</p>}
                  <dl className="my-dl">
                    <dt>Rayon</dt><dd>{p.categorie}</dd>
                    {p.origine && <><dt>Origine</dt><dd>{p.origine}</dd></>}
                    {p.conservation && <><dt>Conservation</dt><dd>{p.conservation}</dd></>}
                    {p.allergenes && <><dt>Allergènes</dt><dd>{p.allergenes}</dd></>}
                  </dl>
                </div>
                <Card as="section" aria-labelledby="titre-cond">
                  <div className="my-pile">
                    <h2 id="titre-cond" className="my-titre-4">Conditionnements</h2>
                    <ul className="my-pile my-pile--serre" style={{ listStyle: 'none', padding: 0 }}>
                      {(p.conditionnements ?? []).map((c) => (
                        <li key={c.type} className="my-ligne my-ligne--espace">
                          <span><strong>{LIB_COND[c.type!]}</strong>{c.type === 'PALETTE' && <> <Badge ton="marque">Conditionnement prioritaire</Badge></>}<br />
                            <span className="my-petit my-secondaire">{detailConditionnement({ type: c.type!, unitesBase: c.unitesBase }, p.uniteBase)}</span></span>
                        </li>
                      ))}
                    </ul>
                    {p.disponibilite && <StockIndicator etat={p.disponibilite} />}
                    <div className="my-alert my-alert--info" role="note">
                      <Icon icon={Lock} />
                      <div className="my-pile my-pile--serre">
                        <strong>Prix réservés aux professionnels</strong>
                        <span>Connectez-vous pour voir votre prix, vos remises de volume et la disponibilité par entrepôt.</span>
                      </div>
                    </div>
                    {connecte && estClient
                      ? <Link className="my-btn my-btn--primaire" to={`/pro/catalogue/${p.id}`}>Voir mon prix</Link>
                      : <div className="my-ligne">
                          <Link className="my-btn my-btn--primaire" to={`/connexion?retour=${encodeURIComponent(`/pro/catalogue/${p.id}`)}`}>Se connecter pour voir les prix</Link>
                          <Link className="my-btn my-btn--secondaire" to="/devenir-client">Devenir client</Link>
                        </div>}
                  </div>
                </Card>
              </div>
            </>
          );
        }}
      />
    </div>
  );
}

const schemaContact = z.object({
  nom: z.string().trim().min(2, 'Indiquez votre nom').max(120),
  email: z.string().trim().email('Adresse e-mail invalide'),
  entreprise: z.string().trim().max(200).optional(),
  message: z.string().trim().min(10, 'Votre message est trop court').max(4000, '4000 caractères maximum'),
});

export function Contact() {
  const [envoye, setEnvoye] = useState(false);
  const [erreur, setErreur] = useState<unknown>(null);
  const f = useForm<z.infer<typeof schemaContact>>({ resolver: zodResolver(schemaContact), defaultValues: { nom: '', email: '', entreprise: '', message: '' } });
  const soumettre = f.handleSubmit(async (v) => {
    setErreur(null);
    try {
      await post('/contact', { nom: v.nom, email: v.email, message: v.message, ...(v.entreprise ? { entreprise: v.entreprise } : {}) });
      setEnvoye(true);
      f.reset();
    } catch (e) { setErreur(e); }
  });
  return (
    <div className="my-conteneur" style={{ maxWidth: 720 }}>
      <EntetePage titre="Contact" sousTitre="Une question sur une référence, un tarif ou l’ouverture d’un compte ?" />
      <Card as="section">
        <form className="my-form" onSubmit={soumettre} noValidate>
          {envoye && <Alert ton="succes" titre="Message envoyé">Notre équipe vous répond sous un jour ouvré (simulation : aucun e-mail réel n’est envoyé).</Alert>}
          <ErreurFormulaire erreur={erreur} />
          <div className="my-form-ligne">
            <TextField label="Nom" required autoComplete="name" erreur={f.formState.errors.nom?.message} {...f.register('nom')} />
            <TextField label="Adresse e-mail" type="email" required autoComplete="email" erreur={f.formState.errors.email?.message} {...f.register('email')} />
          </div>
          <TextField label="Entreprise (facultatif)" autoComplete="organization" erreur={f.formState.errors.entreprise?.message} {...f.register('entreprise')} />
          <TextareaField label="Message" required erreur={f.formState.errors.message?.message} {...f.register('message')} />
          <p className="my-legende my-secondaire">Vos données servent uniquement à répondre à votre demande (données fictives en simulation).</p>
          <Button type="submit" chargement={f.formState.isSubmitting}>Envoyer</Button>
        </form>
      </Card>
    </div>
  );
}
